import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Search, X } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import {
  canApproveHandbookChange,
  canProposeHandbookChange,
  nextHandbookPolicyNumber,
  resolveHandbookBriefingProfile,
} from '../utils/handbookPolicyAccess';
import {
  createHandbookPolicyStub,
  deriveHandbookSearchText,
  subscribeOpenHandbookChangeRequests,
  subscribePublishedHandbookPolicies,
} from '../utils/handbookPoliciesFirestore';
import { handbookTitleSearchHighlight } from '../utils/handbookSearchHighlight';
import type { HandbookChangeRequest, HandbookPolicy } from '../types/handbookPolicy';
import HandbookHubTabs from '../components/people/handbook/HandbookHubTabs';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

function dashCell(v: string): string {
  return v.trim() ? v : '—';
}

function policyMatchesQuery(p: HandbookPolicy, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const blob = deriveHandbookSearchText(p);
  if (blob.includes(n)) return true;
  const tokens = n.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    return tokens.every(t => blob.includes(t));
  }
  return false;
}

const SEARCH_DEBOUNCE_MS = 200;

const PeopleHandbookIndexPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState<HandbookPolicy[]>([]);
  const [openChangeRequests, setOpenChangeRequests] = useState<HandbookChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [reviewQueueOnly, setReviewQueueOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creatingBusy, setCreatingBusy] = useState(false);

  const briefingProfile = resolveHandbookBriefingProfile(user, crmUsers);
  const canPropose = canProposeHandbookChange({ authRole: user?.role, designations: user?.designations });
  const canApprove = canApproveHandbookChange({ authRole: user?.role, briefingProfile });

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!user?.id) {
      setPolicies([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    const authMeta = { userId: user.id, role: user.role, email: user.email ?? undefined };
    const unsub = subscribePublishedHandbookPolicies(
      list => {
        setPolicies(list);
        setLoadError(null);
        setLoading(false);
      },
      err => {
        setLoadError(err);
        setLoading(false);
      },
      authMeta,
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  useEffect(() => {
    if (!user?.id) {
      setOpenChangeRequests([]);
      return;
    }
    const unsub = subscribeOpenHandbookChangeRequests(
      setOpenChangeRequests,
      () => setOpenChangeRequests([]),
      { userId: user.id, role: user.role, email: user.email ?? undefined },
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  const openByPolicyId = useMemo(() => {
    const map = new Map<string, HandbookChangeRequest[]>();
    for (const cr of openChangeRequests) {
      const list = map.get(cr.policyId) || [];
      list.push(cr);
      map.set(cr.policyId, list);
    }
    return map;
  }, [openChangeRequests]);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const p of policies) {
      if (p.category?.trim()) s.add(p.category.trim());
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [policies]);

  const filteredPolicies = useMemo(() => {
    return policies.filter(p => {
      if (reviewQueueOnly && !openByPolicyId.has(p.id)) return false;
      if (categoryFilter && (p.category || '').trim() !== categoryFilter) return false;
      return policyMatchesQuery(p, debouncedSearch);
    });
  }, [policies, debouncedSearch, categoryFilter, reviewQueueOnly, openByPolicyId]);

  const onSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const createPolicy = async () => {
    const title = newTitle.trim();
    if (!title) {
      setCreateError('A policy title is required.');
      return;
    }
    if (!user) return;
    const policyNumber = nextHandbookPolicyNumber(policies.map(p => p.policyNumber));
    setCreatingBusy(true);
    setCreateError(null);
    try {
      await createHandbookPolicyStub({
        policyNumber,
        title,
        actor: { uid: user.id, name: user.name || user.email || 'User' },
      });
      setCreating(false);
      setNewTitle('');
      navigate(`/people/handbook/${policyNumber}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not create this policy.');
    } finally {
      setCreatingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="people-hub-route-root people-hub-handbook-route-root">
        <div className="ops-hub-toolbar">
          <div className="ops-hub-toolbar-left">
            <h2 className="ops-hub-title people-handbook-index-hub-title">
              <BookOpen size={20} aria-hidden className="people-handbook-hub-title-icon" />
              Company handbook
            </h2>
          </div>
        </div>
        <div className="ops-hub-loading">
          <div className="ops-hub-loading-bar" />
        </div>
      </div>
    );
  }

  return (
    <div className="people-hub-route-root people-hub-handbook-route-root">
      <div className="ops-hub-toolbar people-handbook-index-toolbar">
        <div className="ops-hub-toolbar-left">
          <div>
            <h2 className="ops-hub-title people-handbook-index-hub-title">
              <BookOpen size={20} aria-hidden className="people-handbook-hub-title-icon" />
              Company handbook
            </h2>
            <p className="people-hub-subtitle">
              Digital policy catalog — published documents live here. Suggest a change; the CEO merges.
            </p>
            <HandbookHubTabs />
          </div>
        </div>
        <div className="ops-hub-toolbar-right people-handbook-toolbar-right">
          <div className="ops-hub-stats people-handbook-index-stats">
            <span className="ops-stat">{policies.length} published</span>
            {canApprove ? (
              <button
                type="button"
                className={`ops-stat people-handbook-queue-btn ${reviewQueueOnly ? 'ops-stat-active' : ''} ${openChangeRequests.length ? 'ops-stat-action' : ''}`}
                onClick={() => setReviewQueueOnly(v => !v)}
              >
                Review queue{openChangeRequests.length ? ` · ${openChangeRequests.length} open` : ''}
              </button>
            ) : openChangeRequests.length > 0 ? (
              <span className="ops-stat">{openChangeRequests.length} open</span>
            ) : null}
            {debouncedSearch.trim() || categoryFilter || reviewQueueOnly ? (
              <span className="ops-stat ops-stat-active">{filteredPolicies.length} shown</span>
            ) : null}
          </div>
          {canPropose ? (
            <button type="button" className="ops-new-btn" onClick={() => setCreating(true)}>
              <Plus size={14} aria-hidden />
              New policy
            </button>
          ) : null}
          <div className="people-handbook-search-field">
            <label htmlFor="people-handbook-search" className="people-handbook-search-label">
              <Search size={14} aria-hidden className="people-handbook-search-icon" />
              <span className="sr-only">Search policies</span>
            </label>
            <input
              id="people-handbook-search"
              type="search"
              className="people-handbook-search-input"
              placeholder="Search policies…"
              value={searchInput}
              onChange={onSearchChange}
              autoComplete="off"
              spellCheck={false}
              aria-label="Search policies"
            />
          </div>
        </div>
      </div>

      {loadError ? (
        <div className="people-hub-body pm-scroll">
          <p className="people-hub-muted">
            Could not load handbook policies. Confirm Firestore indexes are deployed and the collection is seeded.
          </p>
          <p className="people-hub-muted people-handbook-error-detail">{loadError.message}</p>
        </div>
      ) : null}

      {!loadError && policies.length === 0 ? (
        <div className="people-hub-body pm-scroll">
          <p className="people-hub-muted">
            No published handbook policies yet.
            {canPropose ? ' Use New policy to add the first catalog entry.' : ''}
          </p>
        </div>
      ) : null}

      {!loadError && policies.length > 0 ? (
        <div className="people-hub-body pm-scroll">
          {categories.length > 0 ? (
            <div className="people-handbook-category-strip" role="toolbar" aria-label="Filter by category">
              <button
                type="button"
                className={`people-handbook-category-chip ${categoryFilter === null ? 'active' : ''}`}
                onClick={() => setCategoryFilter(null)}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  type="button"
                  className={`people-handbook-category-chip ${categoryFilter === cat ? 'active' : ''}`}
                  onClick={() => setCategoryFilter(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          ) : null}

          <div className="people-handbook-index">
            <div className="people-handbook-ops-grid-wrap">
              <div className="people-handbook-ops-grid-inner">
                <div className="people-handbook-ops-grid-header" aria-hidden>
                  <span className="people-handbook-ops-th">Policy #</span>
                  <span className="people-handbook-ops-th">Policy name</span>
                  <span className="people-handbook-ops-th">Version</span>
                  <span className="people-handbook-ops-th">Effective</span>
                  <span className="people-handbook-ops-th">Location</span>
                </div>
                {filteredPolicies.length === 0 ? (
                  <div className="people-handbook-ops-empty">
                    {reviewQueueOnly ? 'No open change requests.' : 'No policies match your search.'}
                  </div>
                ) : (
                  filteredPolicies.map(row => {
                  const openCrs = openByPolicyId.get(row.id) || [];
                  const firstOpen = openCrs[0];
                  const to = firstOpen && reviewQueueOnly
                    ? `/people/handbook/${row.policyNumber}?cr=${firstOpen.id}`
                    : `/people/handbook/${row.policyNumber}`;
                  const hasBody = Boolean(row.body?.trim());
                  const q = debouncedSearch.trim();
                  return (
                    <Link key={row.id} to={to} className="people-handbook-ops-row">
                      <span className="people-handbook-ops-td people-handbook-ops-num">
                        <span className="people-handbook-index-policy-num">{row.policyNumber}</span>
                      </span>
                      <span className="people-handbook-ops-td people-handbook-ops-name">
                        <span className="people-handbook-index-policy-name">
                          {q ? (
                            <span dangerouslySetInnerHTML={{ __html: handbookTitleSearchHighlight(row.title, q) }} />
                          ) : (
                            row.title
                          )}
                        </span>
                        {hasBody ? (
                          <span className="ops-stat ops-stat-done people-handbook-index-badge" title="Full text available in this app">
                            In app
                          </span>
                        ) : null}
                        {openCrs.length > 0 ? (
                          <span className="ops-stat ops-stat-action people-handbook-index-badge">
                            Open CR{openCrs.length > 1 ? ` · ${openCrs.length}` : ''}
                          </span>
                        ) : null}
                      </span>
                      <span className="people-handbook-ops-td people-handbook-ops-muted">
                        {row.currentVersion ? <span className="ops-stat">v{row.currentVersion}</span> : '—'}
                      </span>
                      <span className="people-handbook-ops-td people-handbook-ops-muted">{dashCell(row.effectiveDate)}</span>
                      <span className="people-handbook-ops-td people-handbook-ops-muted">{dashCell(row.location)}</span>
                    </Link>
                  );
                })
              )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {creating ? (
        <div className="ops-modal-overlay" role="presentation" onClick={() => !creatingBusy && setCreating(false)}>
          <div
            className="ops-modal"
            role="dialog"
            aria-labelledby="people-handbook-new-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="ops-modal-header">
              <h3 id="people-handbook-new-title">New policy</h3>
              <button type="button" className="ops-modal-close" onClick={() => setCreating(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="ops-modal-body">
              <label className="people-handbook-editor-field">
                <span>Title</span>
                <input
                  className="ops-modal-input"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  autoFocus
                  placeholder="Policy name"
                />
              </label>
              {createError ? <p className="people-handbook-editor-error">{createError}</p> : null}
              <div className="people-handbook-editor-actions">
                <button type="button" className="ops-hide-completed-btn" onClick={() => setCreating(false)} disabled={creatingBusy}>
                  Cancel
                </button>
                <button type="button" className="ops-new-btn" onClick={() => void createPolicy()} disabled={creatingBusy}>
                  {creatingBusy ? 'Creating…' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PeopleHandbookIndexPage;
